import { buildBackendAisleName } from 'core/slices/utils/aisleNames';
import {
  INACTIVE_PROMOTION_TASK,
  MISSING_AT_LOCATION_TASK,
  MISSING_IN_STORE_TASK,
  MISSING_PROMOTION_TASK,
  OVERHEAD_FRESHNESS_STATES,
  PRICE_MISMATCH_TASK,
  UNEXPECTED_LOCATION_TASK,
  UNIMPLEMENTED_TASK,
  resolveLegacyTaskType,
} from 'core/slices/utils/constants';
import { pickLocalizedText } from 'core/slices/utils/localizedText';
import { resolveInactivePromotionReason } from 'core/slices/utils/taskIdentifiers';
import i18n from 'i18next';
import React from 'react';

import { Box, Typography } from '@mui/material';
import AppText from '../utils/AppText';

const VALUE_SEPARATOR = ' · ';

// Value class per freshness state. The `!` modifier is required: Tailwind emits
// colour utilities alphabetically, so an unprefixed class would lose the cascade
// against the colour baked into the AppText variant.
const OVERHEAD_STATE_CLASS = {
  [OVERHEAD_FRESHNESS_STATES.missing]: 'italic !text-muted',
  [OVERHEAD_FRESHNESS_STATES.fresh]: '',
  [OVERHEAD_FRESHNESS_STATES.stale]: '!text-warning-text',
};

// Label of the detection row per task type; unlisted types use the default.
const DETECTED_LABELS = {
  [PRICE_MISMATCH_TASK]: ['bruno.tasks_details.price_mismatch_detected', 'Price mismatch detected'],
};
const DEFAULT_DETECTED_LABEL = ['bruno.tasks_details.out_detected', 'Out detected'];

export { DetailRow } from '../templates/DetailBlock';

export function checkStock(stock_status) {
  if (stock_status === null || stock_status === undefined) return '-';
  const val =
    stock_status.stock_available !== undefined
      ? stock_status.stock_available
      : stock_status.stock_on_hand !== undefined
        ? stock_status.stock_on_hand
        : null;
  return val === null ? '-' : val;
}

export function getAisleDisplay(task, t) {
  const { landmark, aisle, orientation, aisle_part, client_aisle_name } = task;
  const type = landmark ? 'landmark' : 'aisle';
  const title = t(`bruno.tasks_details.${type}`, type === 'landmark' ? 'Landmark' : 'Aisle');
  const fullAisle = buildBackendAisleName(aisle, orientation, aisle_part);
  return { title, value: landmark || client_aisle_name || fullAisle || '-' };
}

function isEmptyIdentifier(value) {
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  return s === '' || s === '0';
}

export function getOperationalIdentifier(task, t) {
  if (!isEmptyIdentifier(task?.item)) {
    return { label: t('bruno.tasks_details.item', 'Item'), value: task.item };
  }
  if (!isEmptyIdentifier(task?.ean)) {
    return { label: t('bruno.tasks_details.barcode', 'CB'), value: task.ean };
  }
  return { label: t('bruno.tasks_details.item', 'Item'), value: task?.item ?? '-' };
}

export const titleSx = {
  textTransform: 'uppercase',
  lineHeight: 1.3,
  letterSpacing: 0.3,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: { xs: 2, md: 1 },
  fontSize: { xs: '0.7rem', md: '0.875rem' },
  minHeight: 'auto',
  color: 'text.primary',
};

export const captionSx = { lineHeight: 1.8, fontSize: { xs: '0.7rem', md: '0.75rem' } };

const rowSx = { lineHeight: 1.8, fontSize: { xs: '0.7rem', md: '0.75rem' } };

export function CardRow({ label, value, valueColor, labelWidth }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ ...rowSx, minWidth: labelWidth || { xs: '5.5rem', md: '7rem' }, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" color={valueColor || 'text.secondary'} sx={rowSx}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Localized API strings ─────────────────────────────────────────────────────

/**
 * Picks the string for the active language out of a multilingual API dict.
 *
 * The lookup rules live in `core/slices/utils/localizedText`; this only binds
 * the platform's active language.
 */
export function pickApiText(textByLanguage) {
  return pickLocalizedText(textByLanguage, i18n.language);
}

// ── Overhead freshness ────────────────────────────────────────────────────────


export function getOverheadLabel(t) {
  return t('bruno.tasks_details.overhead', 'Overhead');
}

/**
 * Builds the value of the single Overhead row from the backend-computed state.
 * Returns null when the task carries no overhead information at all.
 */
export function getOverheadDisplay(task, t) {
  const freshness = task?.overhead_freshness;
  const landmark = task?.overhead_landmark;

  // Payload without the computed state: fall back to the bare landmark so an
  // older API response still renders the row instead of dropping it.
  if (!freshness || typeof freshness !== 'object') {
    return landmark === undefined
      ? null
      : { state: OVERHEAD_FRESHNESS_STATES.fresh, text: landmark || '-' };
  }

  if (freshness.state === OVERHEAD_FRESHNESS_STATES.missing) {
    return {
      state: OVERHEAD_FRESHNESS_STATES.missing,
      text: t('bruno.tasks_details.overhead_not_found', 'not found in recent scans'),
    };
  }

  // elapsed_time_text can be null even while the state is stale, so every part is optional
  // and the separator is only inserted between parts that actually exist.
  const elapsedTime = pickApiText(freshness.elapsed_time_text);
  const isStale = freshness.state === OVERHEAD_FRESHNESS_STATES.stale;
  const state = isStale
    ? OVERHEAD_FRESHNESS_STATES.stale
    : OVERHEAD_FRESHNESS_STATES.fresh;

  if (!landmark && !elapsedTime) return { state, text: '-' };

  if (isStale) {
    const tail = [landmark, elapsedTime].filter(Boolean).join(VALUE_SEPARATOR);
    return { state, text: `${t('bruno.tasks_details.overhead_last_seen', 'last seen')} ${tail}` };
  }

  const seen = elapsedTime ? `${t('bruno.tasks_details.overhead_seen', 'seen')} ${elapsedTime}` : null;
  return { state, text: [landmark, seen].filter(Boolean).join(VALUE_SEPARATOR) };
}

/**
 * Renders an overhead display descriptor.
 * `dense` inherits the compact font size used by the task card rows.
 */
export function OverheadValue({ display, dense = false }) {
  if (!display) return null;
  return (
    <AppText
      variant={dense ? 'bodySmall' : 'body'}
      className={OVERHEAD_STATE_CLASS[display.state]}
      style={dense ? { fontSize: 'inherit' } : undefined}
    >
      {display.text}
    </AppText>
  );
}

// ── Shelf finding capture time ────────────────────────────────────────────────


/**
 * Builds the label and value of the shelf-finding row from the capture fields.
 * Returns null when the capture source could not be decoded.
 */
export function getDetectedDisplay(task, t) {
  // The API removes capture_timestamp and capture_elapsed_time_text from the record when
  // the source cannot be decoded, so this is a presence check, not a null check.
  if (!task || !('capture_elapsed_time_text' in task)) return null;
  const elapsedTime = pickApiText(task.capture_elapsed_time_text);
  if (!elapsedTime) return null;
  const [key, fallback] =
    DETECTED_LABELS[resolveLegacyTaskType(task.task_type)] ?? DEFAULT_DETECTED_LABEL;
  return { label: t(key, fallback), elapsedTime, timestamp: task.capture_timestamp ?? null };
}

/**
 * Renders a shelf-finding descriptor. The raw capture timestamp is exposed as the
 * native tooltip so the absolute date stays one hover away.
 */
export function DetectedValue({ display, dense = false }) {
  if (!display) return null;
  return (
    <AppText
      variant={dense ? 'bodySmall' : 'body'}
      style={dense ? { fontSize: 'inherit' } : undefined}
      title={display.timestamp || undefined}
    >
      {display.elapsedTime}
    </AppText>
  );
}

// ── Task Detail Rows ──────────────────────────────────────────────────────────

const LAST_TIME_SEEN_DISPLAY = [MISSING_AT_LOCATION_TASK, MISSING_IN_STORE_TASK, UNEXPECTED_LOCATION_TASK];
const PARTIAL_LAST_TIME_SEEN_DISPLAY = [];

function checkStockDetail(stock_status, type) {
  if (stock_status && typeof stock_status === 'object') {
    if (type === 'available') {
      return stock_status.stock_available !== undefined
        ? stock_status.stock_available
        : stock_status.stock_on_hand !== undefined ? stock_status.stock_on_hand : null;
    }
    if (type === 'transit') {
      return stock_status.stock_in_transit !== undefined ? stock_status.stock_in_transit : null;
    }
  }
  return null;
}

export function buildDetailRows(task, { placementInformation, categories, t }) {
  const {
    landmark, aisle, orientation, aisle_part, client_aisle_name,
    pos_price, stock_status,
    promotion_type, store_info, main_category, sub_category,
  } = task;
  // Canonical from here down: every check below is the app's own decision, so
  // it must not depend on which name the API is currently sending.
  const task_type = resolveLegacyTaskType(task.task_type);

  const isPromotion = task_type === INACTIVE_PROMOTION_TASK || task_type === MISSING_PROMOTION_TASK;
  const isUnimplemented = task_type === UNIMPLEMENTED_TASK;
  const rows = [];

  const isLandmark = !!landmark;
  const aisleLabel = isLandmark ? t('bruno.tasks_details.landmark', 'Landmark') : t('bruno.tasks_details.aisle', 'Aisle');
  const aisleValue =
    landmark ||
    client_aisle_name ||
    buildBackendAisleName(aisle, orientation, aisle_part) ||
    '-';

  const lastTimeSeen = placementInformation?.[0]?.last_time_seen;
  const partialLTS = placementInformation?.[0]?.parcial_last_time_seen;

  const mainCatLevel = store_info?.main_category_type ? parseInt(store_info.main_category_type.split('_')[1], 10) : null;
  const subCatLevel = store_info?.sub_category_type ? parseInt(store_info.sub_category_type.split('_')[1], 10) : null;
  const mainCatName = categories?.find(c => c.value === main_category && c.level === mainCatLevel)?.name;
  const subCatName = categories?.find(c => c.value === sub_category && c.level === subCatLevel)?.name;

  if (isUnimplemented) {
    if (mainCatName) rows.push({ icon: 'category', label: t('bruno.tasks_details.category', 'Category'), value: mainCatName });
    if (subCatName) rows.push({ icon: 'category', label: t('bruno.tasks_details.subcategory', 'Subcategory'), value: subCatName });
  } else {
    rows.push({ icon: 'location_on', label: aisleLabel, value: aisleValue });
  }

  if (LAST_TIME_SEEN_DISPLAY.includes(task_type) && lastTimeSeen)
    rows.push({ icon: 'schedule', label: t('bruno.tasks_details.last_scan_date', 'Last scan'), value: lastTimeSeen });
  else if (PARTIAL_LAST_TIME_SEEN_DISPLAY.includes(task_type) && partialLTS)
    rows.push({ icon: 'schedule', label: t('bruno.tasks_details.last_scan_date', 'Last scan'), value: partialLTS });

  if (isPromotion) {
    rows.push({ icon: 'local_offer', label: t('bruno.tasks_details.promotion_type', 'Promotion type'), value: promotion_type || store_info?.promotion_type });
    rows.push({ icon: 'event', label: t('bruno.tasks_details.start', 'Start'), value: store_info?.promotion_start_date });
    rows.push({ icon: 'event_busy', label: t('bruno.tasks_details.end', 'End'), value: store_info?.promotion_end_date });
    const inactiveReason = resolveInactivePromotionReason(task);
    if (inactiveReason) {
      rows.push({ icon: 'help_outline', label: t('bruno.tasks_details.inactive_reason', 'Reason'), value: t(`bruno.promotion_reason.${inactiveReason}`, inactiveReason) });
    }
  } else {
    // Price tasks list every price of the shelf label in their own table, so a
    // lone POS price here would contradict it when there are several. Other task
    // types keep showing it as a plain product attribute.
    if (pos_price != null && task_type !== PRICE_MISMATCH_TASK)
      rows.push({ icon: 'sell', label: t('bruno.tasks_details.pos_price', 'POS Price'), value: pos_price });
    const avail = checkStockDetail(stock_status, 'available');
    const transit = checkStockDetail(stock_status, 'transit');
    if (avail != null) rows.push({ icon: 'bar_chart', label: t('bruno.tasks_details.stock', 'Stock'), chip: avail });
    if (transit != null) rows.push({ icon: 'sync_alt', label: t('bruno.tasks_details.transit_stock', 'Stock in transit'), chip: transit });
  }

  // Overhead and the shelf finding sit next to each other so the two elapsed times can be
  // compared in a single glance.
  const overheadDisplay = getOverheadDisplay(task, t);
  if (overheadDisplay)
    rows.push({ icon: 'view_in_ar', label: getOverheadLabel(t), value: <OverheadValue display={overheadDisplay} /> });

  const detectedDisplay = getDetectedDisplay(task, t);
  if (detectedDisplay)
    rows.push({ icon: 'schedule', label: detectedDisplay.label, value: <DetectedValue display={detectedDisplay} /> });

  return rows;
}

// ── Task Detail Modal ─────────────────────────────────────────────────────────

export const STATUS_COLOR = '#1a242d';

export const STATUS_ICONS = {
  done: { icon: 'check_circle' },
  completed: { icon: 'check_circle' },
  accepted: { icon: 'thumb_up' },
  rejected: { icon: 'thumb_down' },
  on_offer: { icon: 'local_offer' },
  on_sale: { icon: 'sell' },
  inactive_promotion: { icon: 'local_offer' },
  verify_catalog: { icon: 'fact_check' },
  check_catalog: { icon: 'menu_book' },
  check_stock: { icon: 'bar_chart' },
  stock_unavailable: { icon: 'move_to_inbox' },
  out_of_stock: { icon: 'remove_shopping_cart' },
  not_found: { icon: 'search_off' },
  error: { icon: 'error' },
  price_ok: { icon: 'price_check' },
  replenished: { icon: 'shopping_cart' },
  request_packdown: { icon: 'low_priority' },
  occluded_label: { icon: 'label_off' },
  catalogued: { icon: 'library_add_check' },
  uncatalogued: { icon: 'library_add' },
};

export function getStatusStyle(status) {
  return STATUS_ICONS[status] || { icon: 'task_alt' };
}

export const DONE_STATUSES = new Set(['done', 'completed', 'accepted', 'price_ok', 'replenished', 'catalogued', 'on_offer', 'on_sale']);

export function getStatusColor(status) {
  return DONE_STATUSES.has(status) ? '#46bf83' : '#fe2d38';
}

export function StatusBadge({ status, label }) {
  const color = getStatusColor(status);
  const { icon } = getStatusStyle(status);
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      mt: 0.75, px: 1.25, py: 0.4, borderRadius: 1.5,
      bgcolor: `${color}1a`,
    }}>
      <span className="material-icons" style={{ fontSize: 13, color }}>{icon}</span>
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color }}>
        {label}
      </Typography>
    </Box>
  );
}


// Shared with mobile, so the counting rules live in core as the single source.
export { countActiveFilters, countNarrowingFilters } from 'core/slices/utils/filterCounts';

/**
 * Returns spread_to_fill filter options for the TasksFilters modal.
 * Encapsulates translation keys and values in a single reusable definition.
 */
export function getSpreadToFillOptions(t) {
  return [
    { label: t('bruno.tasks.spread_to_fill_all', 'All'), value: null },
    { label: t('bruno.tasks.spread_to_fill_full', 'Full'), value: false },
    { label: t('bruno.tasks.spread_to_fill_partial', 'Partial'), value: true },
  ];
}
